import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import { getPixels } from '~/utils/css-units';
import { parseTimeToSeconds } from '../utils/index.ts';
import type { ChapterMarkersPlugin } from '../types.ts';

const parse = (config: any) => {
  return {
    enabled: config?.skip_button_enabled || true,
    persistent: config?.skip_button_persistent || true,
    styles: [
      'bg-player-skip-button',
      'hover:bg-player-skip-button-focus',
      'border-solid',
      'border-player-skip-button-width',
      'border-player-skip-button-color',
      'hover:border-player-skip-button-color-focus',
      'rounded-player-skip-button',
      'font-player-skip-button',
      'text-player-skip-button-font-size',
      'leading-player-skip-button',
      'tracking-player-skip-button',
      'text-player-skip-button-color',
      'hover:text-player-skip-button-color-focused',
      'player-skip-button-text-transform',
      'pt-player-skip-button-t',
      'pr-player-skip-button-r',
      'pb-player-skip-button-b',
      'pl-player-skip-button-l',
      'mt-player-skip-button-t',
      'mr-player-skip-button-r',
      'mb-player-skip-button-b',
      'ml-player-skip-button-l',
    ],
  };
};

function chapterMarkersPlugin(
  this: Player,
  {
    playerID,
    playableItem,
    configuration,
    setPlayNextOverlayDuration,
  }: ChapterMarkersPlugin
) {
  const chapters = playableItem?.extensions?.chapter_markers?.chapters;
  const { enabled, persistent, styles } = parse(configuration);

  if (!playerID || !chapters || !Array.isArray(chapters) || !enabled) return;

  const chapterMarkers = chapters.map((chapter) => ({
    ...chapter,
    start_time: parseTimeToSeconds(chapter.start_time),
    end_time: parseTimeToSeconds(chapter.end_time),
  }));

  const playNextChapterMarker = chapterMarkers.find(({ actions }) =>
    actions?.some(({ type }) => type === 'show_play_next')
  );

  const player: any = videojs(playerID);
  const Button: any = videojs.getComponent('Button');
  const button = new Button(this, {
    className: `absolute right-0 h-auto w-auto select-none vjs-visible-text vjs-hidden`,
  });

  button.addClass(...styles);

  this.addChild(button);

  const seekTo = (time: number) => () => {
    this.currentTime(time);
  };

  const updateSkipButtonState = () => {
    const currentTime = this.currentTime();

    const chapter = currentTime
      ? chapterMarkers.find(
          ({ start_time, end_time }) =>
            start_time <= currentTime && currentTime < end_time
        )
      : null;

    const action = chapter?.actions?.find(({ type }) => type === 'show_skip');

    if (chapter && action) {
      if (button.el().id !== chapter.id) {
        const title = action.options?.title || `Skip ${chapter.title || ''}`;

        button.el().id = chapter.id;
        button.controlText(title);
        button.off('click');
        button.on('click', seekTo(chapter.end_time));

        button.show();
      }

      if (button.hasClass('vjs-hidden')) {
        button.show();
      }
    } else {
      button.hide();
    }
  };

  let controlBarHeight = 0;

  this.on('loadedmetadata', () => {
    controlBarHeight = player.controlBar?.currentHeight?.() || 0;
    button.el().style.bottom = getPixels(controlBarHeight);

    if (playNextChapterMarker) {
      setPlayNextOverlayDuration(
        playNextChapterMarker.end_time - playNextChapterMarker.start_time
      );
    }
  });

  this.on('timeupdate', updateSkipButtonState);

  player.on('useractive', function () {
    if (persistent) {
      button.el().style.transition = 'transform 0.5s';
      button.el().style.transform = 'translateY(0)';
    } else {
      button.el().style.transition = 'opacity 0.5s';
      button.el().style.opacity = '100';
    }
  });

  player.on('pause', () => {
    if (persistent) {
      button.el().style.transform = 'translateY(0)';
    } else {
      button.el().style.opacity = '100';
    }
  });

  player.on('userinactive', function () {
    if (player.paused()) return;

    if (persistent) {
      button.el().style.transition = 'transform 1s';
      button.el().style.transform = `translateY(${getPixels(
        controlBarHeight
      )})`;
    } else {
      button.el().style.transition = 'opacity 1s';
      button.el().style.opacity = '0';
    }
  });
}

export default chapterMarkersPlugin;
